import isEqual from 'lodash/isEqual';
import React, {
  useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import {
  Alert,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  Typography,
  Button,
} from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { useConfigService, useStore } from '@thd-nucleus/experience-context';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useNavigate } from '@thd-olt-component-react/router';
import { Row, Placeholder } from '@thd-olt-component-react/core-ui';
import {
  func, bool, string, arrayOf, shape
} from 'prop-types';
import {
  QueryProvider
} from '@thd-nucleus/data-sources';
import { Hydrator } from '@thd-olt-component-react/hydrator';
import { DynamicComponent } from '@thd-nucleus/app-render';
import { getLocalizerZipCode } from '../../../../utils/fulfillment-utils';
import { useCabinetVisualizerStore, useCabinetVisualizerDigitalDataStore } from '../../../../store';
import { AdvancedFilters as Filters } from './CabinetDrawerComponents/filters';
import { useGetCollections } from '../../../../pages/cabinetVisualizer/hooks/useGetCollections';
import { useIsMobileDevice } from '../../../../hooks/useIsMobileDevice';
import '../../../../styles/cabinetVisualizer/cabinetCollectionDrawer.css';
import {
  getFiltersFromLocalStorage,
  saveCabinetsCategoryAndFiltersInLocalStorage
} from '../../../../pages/collection/common';
import { createTempDesign } from '../../../../utils/createTempDesign';
import { CABINET_VISUALIZER_DIGITAL_DATA, NEW_RELIC_EVENTS } from '../../../../constants';
import { extractProductNameFromUrl } from './extractProductNameFromUrl';
import ResponsiveCabinetCollectionCard from './CabinetDrawerComponents/ResponsiveCabinetCollectionCard';
import DrawerTipMessage from '../../../Drawer/DrawerTipMessage';
import { useDrawerStore } from '../../../../store/ui';
import { getProductIdFromPath } from '../../../../utils/queryParams';
import SkeletonCollectionCard from './CabinetDrawerComponents/SkeletonCollectionCard';

/** Number of matching collections to display per page */
const MATCHING_COLLECTIONS_PAGE_SIZE = 5;

export const CabinetCollectionDrawer = React.memo(({
  designId,
  open,
  onClose,
  onSelectCollection,
  isMatchingMode,
  isMatchingLoading,
  matchingCollections,
  onApplyMatchingCollection,
}) => {
  const store = useStore();
  const { storeId } = store;
  const zipCode = getLocalizerZipCode();
  const isMobile = useIsMobileDevice();
  const {
    trackCabinetCollectionSelectionOverlayEvent,
    trackCollectionViewDetailsEvent,
    trackCollectionRefinementsFiltersEvent,
    trackCabinetCollectionSelectionInteractionEvent,
  } = useCabinetVisualizerDigitalDataStore();
  const hasTrackedRef = useRef(false);
  const [drawerElement, setDrawerElement] = useState(null);
  const drawerCallbackRef = useCallback((node) => {
    setDrawerElement(node);
  }, []);
  const productIdFromPath = getProductIdFromPath();
  const shouldShowCloseButton = !!productIdFromPath;

  useEffect(() => {
    if (open && !hasTrackedRef.current) {
      trackCabinetCollectionSelectionOverlayEvent({
        eventName: CABINET_VISUALIZER_DIGITAL_DATA.CONFIGURATOR.CHOOSE_CABINET_COLLECTION
      });
      hasTrackedRef.current = true;
    }
    if (!open) {
      hasTrackedRef.current = false;
    }
  }, [open]);

  const navigate = useNavigate();
  const {
    cabinetVisualizer,
    updateCabinetVisualizer,
    setUserSelectedCollection,
    prevAnalyticsFilterGroups = {},
    setPrevAnalyticsFilterGroups,
    hasFiredInitialRefinementsEvent,
    setHasFiredInitialRefinementsEvent
  } = useCabinetVisualizerStore();
  const drawerTitle = isMatchingMode ? 'Change Cabinet Collection' : 'Choose Cabinet Collection';
  const rawHost = useConfigService('global-custom-commerce:host');
  const host = useMemo(() => rawHost, [rawHost]);
  const collectionCardContainerClasses = classNames(
    'sui-grid sui-w-full sui-gap-4 sui-px-1 sui-max-w-full sui-box-border',
    'sui-grid-cols-1',
    'sm:sui-grid-cols-[repeat(auto-fill,minmax(min(500px,100%),1fr))]'
  );

  const omsId = '334219672';
  const [dataSource, setDataSource] = useState(null);
  const [filteredDataSource, setFilteredDataSource] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    filters: [],
    filterNames: []
  });
  const collections = useDrawerStore((state) => state.collections);
  const getLeftViewImageUrl = useDrawerStore((state) => state.getLeftViewImageUrl);
  const setSelectedCollection = useDrawerStore((state) => state.setSelectedCollection);
  const currentCollectionId = useCabinetVisualizerStore((state) => {
    const selectedItemId = getProductIdFromPath() || state.cabinetVisualizer.selectedItemId;
    return selectedItemId;
  });
  const [primaryFilters, setPrimaryFilters] = useState([]);
  const [advancedFilters, setAdvancedFilters] = useState(getFiltersFromLocalStorage(omsId) || []);

  const [selectedCategory, setSelectedCategory] = useState(''); // getCategoryFromLocalStorage(omsId)
  const [analyticsFilterGroups, setAnalyticsFilterGroups] = useState(null);
  // Matching mode pagination state
  const [matchingDisplayCount, setMatchingDisplayCount] = useState(MATCHING_COLLECTIONS_PAGE_SIZE);

  // Filter out the currently selected collection from the collections list
  const filteredCollections = useMemo(() => {
    if (!collections || !currentCollectionId) {
      return collections?.products || collections;
    }

    // In matching mode, filter from matchingCollections
    if (isMatchingMode && matchingCollections) {
      return matchingCollections.filter(
        (collection) => collection.collectionId !== String(currentCollectionId)
      );
    }

    return collections.products?.filter(
      (collection) => String(collection.itemId) !== String(currentCollectionId)
    ) || collections.products;
  }, [collections, matchingCollections, currentCollectionId, isMatchingMode]);

  // Reset matching display count when drawer opens in matching mode
  useEffect(() => {
    if (open && isMatchingMode) {
      setMatchingDisplayCount(MATCHING_COLLECTIONS_PAGE_SIZE);
    }
  }, [open, isMatchingMode]);

  const matchingDataSource = useMemo(() => {
    if (!isMatchingMode || !matchingCollections) {
      return null;
    }

    const collectionsToProcess = filteredCollections && isMatchingMode
      ? filteredCollections
      : matchingCollections;

    // Helper function to format product detail URL with cabinet-seller path
    const getProductDetailUrl = (canonicalUrl) => {
      if (canonicalUrl?.includes('/p/cabinet-seller/')) {
        return canonicalUrl;
      }
      const updatedCanonicalUrl = canonicalUrl?.replace('/p/', '/p/cabinet-seller/');
      return updatedCanonicalUrl || '#';
    };

    // Helper function to lookup images from cached collections by collectionId
    const getCollectionImages = (collectionId) => {
      if (!collections?.products) {
        return { primaryURL: '', secondaryURL: null };
      }

      const product = collections.products.find((prd) => prd.itemId === collectionId);
      if (!product?.media?.images) {
        return { primaryURL: '', secondaryURL: null };
      }

      // Get PRIMARY image
      const primaryImg = product.media.images.find((img) => img.subType === 'PRIMARY');
      const primaryURL = primaryImg?.url?.replace('<SIZE>', '400') || '';

      // Get IMAGE_LEFT_VIEW image
      const leftViewImg = product.media.images.find((img) => img.type === 'IMAGE_LEFT_VIEW');
      const secondaryURL = leftViewImg?.url?.replace('<SIZE>', '400') || null;

      return { primaryURL, secondaryURL };
    };

    // Helper function to lookup canonical URL from cached collections by collectionId
    const getCollectionCanonicalUrl = (collectionId) => {
      if (!collections?.products) {
        return '#';
      }

      const product = collections.products.find((prd) => prd.itemId === collectionId);
      return product?.canonicalUrl || '#';
    };

    const result = collectionsToProcess.map((collection) => {
      // Get a sample SKU from the matched collection for pricing lookup
      // Use the first value from itemIdMap as the sample item
      const sampleItemIds = collection.itemIds || [];
      const sampleSku = sampleItemIds.length > 0 ? sampleItemIds[0] : null;

      // Lookup images and canonical URL from cached collections
      const { primaryURL, secondaryURL } = getCollectionImages(collection.collectionId);
      const canonicalUrl = getCollectionCanonicalUrl(collection.collectionId);

      return {
        productLabel: collection.collectionName || 'N/A',
        brandName: collection.brandName || 'N/A',
        productURL: getProductDetailUrl(canonicalUrl), // PDP URL with cabinet-seller path
        canonicalUrl, // Store original canonical URL
        primaryURL,
        secondaryURL,
        designStyleChoiceId: collection.designStyleChoiceId,
        itemId: collection.collectionId,
        colorFinish: collection.colorFinish || collection.colorFamily,
        startingAtPrice: collection.startingAtPrice,
        sampleBaseCabinetItemId: sampleSku, // Add sample SKU for pricing lookup
        matchingRecordsCount: collection.matchingRecordsCount,
        itemIdMap: collection.itemIdMap,
        // Keep original collection data for applying mapping
        originalCollection: collection,
      };
    });

    return result;
  }, [isMatchingMode, matchingCollections, collections, filteredCollections]);

  // Get visible matching collections based on pagination
  const visibleMatchingCollections = useMemo(() => {
    if (!matchingDataSource) {
      return null;
    }
    const result = matchingDataSource.slice(0, matchingDisplayCount);

    return result;
  }, [matchingDataSource, matchingDisplayCount]);

  // Check if there are more matching collections to load
  const hasMoreMatchingCollections = useMemo(() => {
    if (!matchingDataSource) {
      return false;
    }
    const result = matchingDataSource && matchingDisplayCount < matchingDataSource.length;

    return result;
  }, [matchingDataSource, matchingDisplayCount]);

  // Handle Load More button click
  const handleLoadMoreCollections = useCallback(() => {
    setMatchingDisplayCount((prev) => prev + MATCHING_COLLECTIONS_PAGE_SIZE);
  }, []);

  /**
   * Handles applying filters to data source for the cabinet collection drawer.
   *
   * This function processes the selected filters, groups them by filter name,
   * retrieves the corresponding itemIds for each filter type (e.g., Brand Name, Color Finish Family, Door Style),
   * and filters the dataSource by the intersection of all active filter itemIds.
   * The filtered results are then set to the filteredDataSource state, and the selected filters
   * are stored in the advancedFilters state.
   *
   * @param {string[]} selectedFilters - An array of selected filter strings in the format "Filter Name - Filter Value".
   * @param {object[]} defaultDataSource - optional data source to filter. If not provided, uses the current dataSource.
   * @param {object[]} defaultFilters - optional default filters to use, if not provided, uses primaryFilters.
   *
   * Example:
   *   selectedFilters = [
   *     "Brand Name - Contractor Express",
   *     "Brand Name - J Collection",
   *     "Color Finish Family - White"
   *   ]
   *
   * The function supports multiple selections for each filter type and ensures that only items
   * matching all selected filter criteria are shown.
   */
  const applyFiltersToDataSource = (selectedFilters, defaultDataSource, defaultFilters) => {
    // Group selected values by filter name
    const filterGroups = selectedFilters.reduce((acc, filter) => {
      const [filterName, filterValue] = filter.split(' - ');
      if (!acc[filterName]) acc[filterName] = [];
      acc[filterName].push(filterValue);
      return acc;
    }, {});

    setAnalyticsFilterGroups(filterGroups);

    const filters = defaultFilters || primaryFilters;
    // Helper to get all itemIds for a filter type (union of all selected values)
    const getItemIdsForFilter = (filterLabel, selectedValues) => {
      const filter = filters.find((fl) => fl.label === filterLabel);
      if (!filter) return [];
      return filter.refinements
        .filter((refinement) => selectedValues.includes(refinement.label))
        .flatMap((refinement) => refinement.itemIds);
    };

    // Get itemIds for each filter type
    const brandItemIds = filterGroups['Brand Name']
      ? getItemIdsForFilter('Brand Name', filterGroups['Brand Name'])
      : null;
    const colorItemIds = filterGroups['Color Finish Family']
      ? getItemIdsForFilter(
        'Color Finish Family',
        filterGroups['Color Finish Family']
      )
      : null;
    const doorStyleItemIds = filterGroups['Door Style']
      ? getItemIdsForFilter('Door Style', filterGroups['Door Style'])
      : null;

    // Filter dataSource by intersection of all active filter itemIds
    const baseDataSource = defaultDataSource
    || (isMatchingMode && visibleMatchingCollections?.length > 0
      ? visibleMatchingCollections
      : dataSource);
    let filteredData = baseDataSource;
    const activeFilters = [brandItemIds, colorItemIds, doorStyleItemIds].filter(Boolean);
    if (activeFilters.length > 0) {
      const commonItemIds = activeFilters.reduce((acc, ids) => acc.filter((id) => ids.includes(id))
      );
      filteredData = baseDataSource.filter((item) => commonItemIds.includes(item.itemId)
      );
    }
    setFilteredDataSource(filteredData);
  };

  const lastAnalyticsFilterGroupsRef = useRef(prevAnalyticsFilterGroups);

  useEffect(() => {

    const isAnalyticsGroupObject = analyticsFilterGroups
      && typeof analyticsFilterGroups === 'object'
      && !Array.isArray(analyticsFilterGroups);

    // Initial fire: delay analytics event
    const hasSavedFilters = isAnalyticsGroupObject && Object.keys(analyticsFilterGroups).length > 0;
    if (
      isAnalyticsGroupObject
      && !hasFiredInitialRefinementsEvent
    ) {
      if (hasSavedFilters) {
        // do not fire empty even on load
        trackCollectionRefinementsFiltersEvent({
          filterGroups: analyticsFilterGroups,
          delayed: true
        });
      }
      setHasFiredInitialRefinementsEvent(true);
      setPrevAnalyticsFilterGroups(analyticsFilterGroups);
      return;
    }

    // User-initiated filter changes (no delay)
    if (
      isAnalyticsGroupObject
      && hasFiredInitialRefinementsEvent
      && !isEqual(prevAnalyticsFilterGroups, analyticsFilterGroups)
      && !isEqual(lastAnalyticsFilterGroupsRef.current, analyticsFilterGroups)
    ) {
      trackCollectionRefinementsFiltersEvent({
        filterGroups: analyticsFilterGroups,
        delayed: false
      });
      setPrevAnalyticsFilterGroups(analyticsFilterGroups);
      lastAnalyticsFilterGroupsRef.current = analyticsFilterGroups;
    }
  }, [
    analyticsFilterGroups,
    prevAnalyticsFilterGroups,
    hasFiredInitialRefinementsEvent,
    setPrevAnalyticsFilterGroups,
    trackCollectionRefinementsFiltersEvent
  ]);

  const updateAdvancedFilters = useCallback((event, selectedFilters) => {
    event.preventDefault();
    event.stopPropagation();

    saveCabinetsCategoryAndFiltersInLocalStorage(omsId, selectedCategory, selectedFilters);

    if (selectedFilters.length === 0) {
      setFilteredDataSource(null);
      setAdvancedFilters([]);
      setAnalyticsFilterGroups({}); // ensure new object reference
      return;
    }

    applyFiltersToDataSource(selectedFilters);
    setAdvancedFilters(selectedFilters);

  }, [
    dataSource,
    primaryFilters,
    collections,
    omsId,
    selectedCategory,
    trackCollectionRefinementsFiltersEvent,
    isMatchingMode,
    matchingCollections,
    visibleMatchingCollections
  ]);

  const getProductDetailUrl = (canonicalUrl) => {
    if (canonicalUrl?.includes('/p/cabinet-seller/')) {
      return canonicalUrl;
    }
    const updatedCanonicalUrl = canonicalUrl?.replace('/p/', '/p/cabinet-seller/');
    return updatedCanonicalUrl || '#';
  };

  const getProductImageUrl = (product) => {
    if (product?.media?.images?.some((img) => img.subType === 'PRIMARY')) {
      return product.media.images.find((img) => img.subType === 'PRIMARY').url.replace('<SIZE>', '400');
    }
    if (product?.media?.images?.length > 0) {
      return product.media.images[0].url.replace('<SIZE>', '400');
    }
    return '';
  };

  const getSecondaryImageUrl = (product) => {
    if (product?.media?.images?.some((img) => img.type === 'IMAGE_LEFT_VIEW')) {
      return product.media.images.find((img) => img.type === 'IMAGE_LEFT_VIEW').url.replace('<SIZE>', '400');
    }
    return null;
  };

  const getBrandNameFilters = () => {
    const brandMap = collections?.products?.reduce((acc, product) => {
      const brand = product.brandName || 'N/A';
      const itemId = product.itemId;
      if (!acc[brand]) {
        acc[brand] = { itemIds: [], count: 0 };
      }
      acc[brand].itemIds.push(itemId);
      acc[brand].count += 1;
      return acc;
    }, {});

    // To get distinct brand names:
    const distinctBrandNames = Object.keys(brandMap);
    const displayLabel = isMobile ? 'Brand' : 'Brand Name';
    const brandFilters = { label: 'Brand Name', displayLabel, refinements: [] };
    brandFilters.refinements = distinctBrandNames.map((brand) => ({
      label: brand,
      recordCount: brandMap[brand].count,
      itemIds: brandMap[brand].itemIds
    }));

    return brandFilters;
  };

  const getColorFinishFilters = () => {
    const colorMap = collections?.products?.reduce((acc, product) => {
      const color = product.colorFinishFamily || 'N/A';
      const itemId = product.itemId;
      if (!acc[color]) {
        acc[color] = { itemIds: [], count: 0 };
      }
      acc[color].itemIds.push(itemId);
      acc[color].count += 1;
      return acc;
    }, {});

    // To get distinct color names:
    const distinctColorNames = Object.keys(colorMap);

    const displayLabel = isMobile ? 'Color' : 'Color Finish Family';
    const colorFilters = { label: 'Color Finish Family', displayLabel, refinements: [] };
    colorFilters.refinements = distinctColorNames.map((color) => ({
      label: color,
      recordCount: colorMap[color].count,
      itemIds: colorMap[color].itemIds
    }));

    return colorFilters;
  };
  // Reset dataSource when drawer closes or when current collection changes
  useEffect(() => {
    if (!open || !currentCollectionId) {
      setDataSource(null);
      setFilteredDataSource(null);
    }
  }, [open, currentCollectionId]);
  useEffect(() => {
    if (collections && collections.products && !dataSource) {
    // Use filteredCollections if not in matching mode
      const collectionsToProcess = !isMatchingMode && filteredCollections
        ? filteredCollections
        : collections.products;

      // Transform JSON data to match component expectations
      const newDataSource = collectionsToProcess?.map((product) => {

        return {
          productLabel: product.productLabel || 'N/A',
          brandName: product.brandName || 'N/A',
          productURL: getProductDetailUrl(product.canonicalUrl),
          primaryURL: getProductImageUrl(product),
          secondaryURL: getSecondaryImageUrl(product),
          designStyleChoiceId: product.designStyleChoiceId,
          itemId: product.itemId,
          leftViewImageUrl: getLeftViewImageUrl(product),
          colorFinish: product.colorFinish || product.color,
          startingAtPrice: product.startingAtPrice,
          sampleBaseCabinetItemId: product.sampleBaseCabinetItemId,
          matchingTemplates: product.matchingTemplates,
        };
      }) || [];

      setDataSource(newDataSource);

      const doorStyleFilters = collections.primaryFilters[0];
      doorStyleFilters.label = 'Door Style';
      doorStyleFilters.displayLabel = 'Door Style';
      const filters = [doorStyleFilters, getBrandNameFilters(), getColorFinishFilters()];
      setPrimaryFilters(filters);

      const filterNames = filters.map((filter) => {
        return { label: filter.label, displayLabel: filter.displayLabel };
      });
      setFilterOptions({
        filters,
        filterNames
      });

      if (advancedFilters && advancedFilters.length > 0) {
        applyFiltersToDataSource(advancedFilters, newDataSource, filters);
      } else {
        setAnalyticsFilterGroups({});
      }
    }
  }, [dataSource, collections, filteredCollections, isMatchingMode]);

  useEffect(() => {
    if (isMatchingMode) {
      setFilteredDataSource(null); // Clear any existing filters in matching mode
      setAdvancedFilters([]);
    }
  }, [isMatchingMode]);

  // Utility to get canonicalUrl from collections by itemId
  const getProductCanonicalUrlByItemId = (itemId) => {
    if (!collections || !collections.products) return undefined;
    const product = collections.products.find((productData) => productData.itemId === itemId);
    return product ? product.canonicalUrl : undefined;
  };

  /**
   * Handle collection selection - different behavior for matching mode vs normal mode
   */
  const isRealDesignId = (designIdArg) => {
  // Check if it's not a temp ID and contains UUID-like hyphens
    return designIdArg
         && !designIdArg.startsWith('temp-')
         && designIdArg.includes('-')
         && designIdArg.length === 36; // UUID length is always 36 characters
  };
  const handleSelectCollection = useCallback(async (collection) => {

    // MATCHING MODE: Apply SKU mapping to existing designs
    if (isMatchingMode && collection.originalCollection && onApplyMatchingCollection) {
      await onApplyMatchingCollection(collection.originalCollection);
      if (onClose) {
        onClose(null);
      }
      return;
    }

    // NORMAL MODE: Standard collection selection behavior
    setSelectedCollection(collection);
    const selectedStyleChoiceid = collection.designStyleChoiceId;
    const selectedItemId = collection.itemId || collection.omsId;
    const numericStyleChoiceId = parseInt(selectedStyleChoiceid, 10);

    if (setUserSelectedCollection) {
      setUserSelectedCollection(numericStyleChoiceId);
    }

    const freshState = useCabinetVisualizerStore.getState().cabinetVisualizer;
    let updatedDesigns = { ...freshState.designs };
    let updatedDesignIds = [...freshState.designIds];
    let updatedStyleDesignIds = [];
    let newSelectedDesignId = selectedItemId;
    const currentProjectId = freshState.designs[designId].ProjectId;

    if (designId && isRealDesignId(designId)) {

      updatedStyleDesignIds = [...freshState.styleDesignIds];

      Object.keys(updatedDesigns).forEach((designIdArg) => {
        if (isRealDesignId(designIdArg)) {
          updatedDesigns[designIdArg] = {
            ...updatedDesigns[designIdArg],
            CollectionId: selectedItemId,
            DesignStyleChoiceid: numericStyleChoiceId,
          };
        }
      });

      if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
        try {
          const storageKey = 'cabinet-design-session-storage';
          const existingStorage = localStorage.getItem(storageKey);

          if (existingStorage) {
            const parsedStorage = JSON.parse(existingStorage);

            // Update the projects object in localStorage
            if (parsedStorage.state && parsedStorage.state.projects) {

              // Update the current project with new collection data
              if (currentProjectId && parsedStorage.state.projects[currentProjectId]) {
                parsedStorage.state.projects[currentProjectId] = {
                  ...parsedStorage.state.projects[currentProjectId],
                  styleChoiceId: numericStyleChoiceId,
                  collectionId: selectedItemId,
                  lastModified: new Date().toISOString()
                };
              }

              // Write back to localStorage
              localStorage.setItem(storageKey, JSON.stringify(parsedStorage));
            }
          }
        } catch (storageError) {
          console.warn('Failed to update localStorage:', storageError);
        // Non-critical error, continue with store update
        }
      }

      const updatedProjects = { ...freshState.projects };
      if (currentProjectId && updatedProjects[currentProjectId]) {
        updatedProjects[currentProjectId] = {
          ...updatedProjects[currentProjectId],
          styleChoiceId: numericStyleChoiceId,
          collectionId: selectedItemId,
          lastModified: new Date().toISOString()
        };
      }

      updateCabinetVisualizer({
        styleChoiceId: numericStyleChoiceId,
        userSelectedStyleChoice: true,
        isLandingPage: false,
        selectedDesignId: newSelectedDesignId,
        designs: updatedDesigns,
        designIds: updatedDesignIds,
        styleDesignIds: updatedStyleDesignIds,
        choices: null,
        choiceMetadata: null,
        projects: updatedProjects,
        skuDrawer: {
          ...freshState.skuDrawer,
          modalAssortment: {},
          superSkuProducts: [],
          selectedItemId: null,
          selectedParentId: null,
        },
        collectionChangeTriggered: true,
      });

      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('DesignStyleChoiceid', selectedStyleChoiceid);
      currentUrl.searchParams.set('designid', designId);

      const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
      if (pathSegments.length && /^[0-9]+$/.test(pathSegments[pathSegments.length - 1])) {
        pathSegments[pathSegments.length - 1] = String(selectedItemId);
      } else {
        pathSegments.push(String(selectedItemId));
      }
      currentUrl.pathname = '/' + pathSegments.join('/');

      navigate(currentUrl.pathname + currentUrl.search, { replace: true });
    } else {
      // Get the current selected item ID from the URL or store
      const currentSelectedItemId = getProductIdFromPath() || freshState.selectedItemId;
      const shouldClearExistingDesigns = selectedItemId !== currentSelectedItemId;

      const existingTempDesigns = Object.keys(updatedDesigns).filter((id) => id.startsWith('temp-'));

      if (existingTempDesigns.length > 0) {
        existingTempDesigns.forEach((tempId) => {
          const oldDesign = updatedDesigns[tempId];
          const newTempId = `temp-${numericStyleChoiceId}`;
          delete updatedDesigns[tempId];
          updatedDesigns[newTempId] = {
            ...oldDesign,
            Id: newTempId,
            DesignStyleChoiceid: numericStyleChoiceId,
            LastModified: new Date().toISOString()
          };
          const tempIndex = updatedDesignIds.indexOf(tempId);
          if (tempIndex !== -1) {
            updatedDesignIds[tempIndex] = newTempId;
          }
          newSelectedDesignId = newTempId;
        });
        updatedStyleDesignIds = updatedDesignIds.filter((id) => {
          const design = updatedDesigns[id];
          return design && parseInt(design.DesignStyleChoiceid, 10) === numericStyleChoiceId;
        });
      } else if (shouldClearExistingDesigns) {
        updatedDesigns = {};
        updatedDesignIds = [];
        const tempId = `temp-${numericStyleChoiceId}`;
        const tempDesign = createTempDesign(numericStyleChoiceId, tempId);
        updatedDesigns[tempId] = tempDesign;
        updatedDesignIds.push(tempId);
        updatedStyleDesignIds = [tempId];
        newSelectedDesignId = tempId;
      }

      updateCabinetVisualizer({
        styleChoiceId: numericStyleChoiceId,
        userSelectedStyleChoice: true,
        isLandingPage: false,
        selectedDesignId: newSelectedDesignId,
        designs: updatedDesigns,
        designIds: updatedDesignIds,
        styleDesignIds: updatedStyleDesignIds,
        choices: null,
        choiceMetadata: null,
        skuDrawer: {
          ...freshState.skuDrawer,
          modalAssortment: {},
          superSkuProducts: [],
          selectedItemId: null,
          selectedParentId: null,
        },
        collectionChangeTriggered: true,
      });

      const currentUrl = new URL(window.location);
      currentUrl.searchParams.set('DesignStyleChoiceid', selectedStyleChoiceid);

      const pathSegments = currentUrl.pathname.split('/').filter(Boolean);
      if (pathSegments.length && /^[0-9]+$/.test(pathSegments[pathSegments.length - 1])) {
        pathSegments[pathSegments.length - 1] = String(selectedItemId);
      } else {
        pathSegments.push(String(selectedItemId));
      }
      currentUrl.pathname = '/' + pathSegments.join('/');

      navigate(currentUrl.pathname + currentUrl.search, { replace: true });
    }

    // Notify parent component about the selection
    if (onSelectCollection) {
      onSelectCollection(collection);
      const canonicalUrl = getProductCanonicalUrlByItemId(selectedItemId);
      const eventTarget = extractProductNameFromUrl(canonicalUrl);
      trackCabinetCollectionSelectionInteractionEvent({ target: eventTarget });
    }

    // Close this drawer to allow the next drawer to open
    if (onClose) {
      onClose(null); // Pass null to indicate this is not a user event
    }
  }, [
    designId,
    isMatchingMode,
    onApplyMatchingCollection,
    onClose,
    setSelectedCollection,
    navigate,
    onSelectCollection,
    trackCabinetCollectionSelectionInteractionEvent,
    setUserSelectedCollection,
    updateCabinetVisualizer,
    cabinetVisualizer.skuDrawer,
    getProductIdFromPath,
    createTempDesign,
    getProductCanonicalUrlByItemId,
    extractProductNameFromUrl
  ]);

  const handleViewDetails = (productURL) => {
    const eventTarget = extractProductNameFromUrl(productURL);
    trackCollectionViewDetailsEvent({ target: eventTarget });
    window.open(productURL, '_blank', 'noopener,noreferrer');
  };

  /**
   * Handle view details for matching mode collections
   * Opens the collection's PDP in a new tab instead of creating a preview project
   */
  const handleViewDetailsForMatchingMode = (collection) => {
    const productURL = collection.productURL || '#';
    const eventTarget = extractProductNameFromUrl(productURL);
    trackCollectionViewDetailsEvent({ target: eventTarget });
    window.open(productURL, '_blank', 'noopener,noreferrer');
  };

  // Create ref object for compatibility with DrawerTipMessage
  const drawerRef = useMemo(() => ({
    current: drawerElement
  }), [drawerElement]);

  // Determine which data source to use for rendering
  const displayDataSource = useMemo(() => {
    if (isMatchingMode) {
      const result = filteredDataSource || visibleMatchingCollections || [];
      return result;
    }

    const result = filteredDataSource || dataSource || [];
    return result;
  }, [isMatchingMode, visibleMatchingCollections, filteredDataSource, dataSource]);

  const hasActiveFilters = advancedFilters && advancedFilters.length > 0;
  const hasNoFilterResults = hasActiveFilters && displayDataSource.length === 0;
  const hasNoMatchingResults = isMatchingMode
  && !isMatchingLoading
  && matchingCollections !== undefined
  && (matchingCollections === null || matchingCollections.length === 0);

  const hasMoreFilteredResults = useMemo(() => {
    if (!isMatchingMode || !hasActiveFilters || !filteredDataSource || !matchingDataSource) {
      return false;
    }

    // When filters are applied, count how many items from the FULL matchingDataSource
    // would match the current filters, and compare to how many we're currently showing
    const allMatchingFilteredItems = matchingDataSource.filter(
      (item) => filteredDataSource.some(
        (filtered) => filtered.itemId === item.itemId
      )
    );

    const totalFilteredCount = allMatchingFilteredItems.length;
    const currentlyShowingCount = filteredDataSource.length;

    return totalFilteredCount > currentlyShowingCount;
  }, [isMatchingMode, hasActiveFilters, filteredDataSource, matchingDataSource]);

  // Combine hasMoreMatchingCollections with filter awareness
  const shouldShowLoadMoreButton = useMemo(() => {
    if (!isMatchingMode) {
      return false;
    }

    // When filters are active, check if there are more filtered results
    if (hasActiveFilters) {
      return hasMoreFilteredResults;
    }

    // Otherwise, check if there are more matching collections to paginate
    return hasMoreMatchingCollections;
  }, [isMatchingMode, hasActiveFilters, hasMoreFilteredResults, hasMoreMatchingCollections]);

  const isLoadingCollections = useMemo(() => {
    if (isMatchingMode) {
      // Matching mode: loading if isMatchingLoading OR no data yet
      return isMatchingLoading || (!matchingCollections && !hasNoMatchingResults);
    }
    // Normal mode: loading if no dataSource yet
    return !dataSource || dataSource.length === 0;
  }, [isMatchingMode, isMatchingLoading, matchingCollections, hasNoMatchingResults, dataSource]);

  return (
    <>
      {(!isMobile && !shouldShowCloseButton && !isMatchingMode) && (
        <DrawerTipMessage
          show={open}
          title={(
            <>
              Select a cabinet<br />
              collection to build<br />
              your layout
            </>
          )}
          content=""
          drawerRef={drawerRef}
        />
      )}
      <Drawer
        open={open}
        position="right"
        onClose={shouldShowCloseButton || isMatchingMode ? (event) => onClose(event) : undefined}
        DrawerRootProps={{ style: { transition: 'right 0.3s ease-in 0s' } }}
      >
        <div className="cabinet-collection-drawer-component" ref={drawerCallbackRef}>

          <DrawerHeader
            title={drawerTitle}
            onClose={shouldShowCloseButton || isMatchingMode ? onClose : undefined}
          />

          <DrawerBody>
            <ErrorBoundary name="cabinet-drawer-eb">
              {/* Alert message for matching mode */}
              {isMatchingMode && (
                <div className="sui-mb-4">
                  <Alert status="info">
                    Select one of the following cabinet collections to update your current cabinets on all walls.
                  </Alert>
                </div>
              )}

              {/* Filters section - only show when not loading */}
              {!isLoadingCollections && (
                <Row>
                  <Filters
                    filterOptions={filterOptions}
                    advancedFilters={advancedFilters}
                    updateAdvancedFilters={updateAdvancedFilters}
                    selectedCategory={selectedCategory}
                    resultCount={filteredDataSource?.length || 0}
                  />
                </Row>
              )}
              {/* Loading State - Show 5 skeleton cards */}
              {isLoadingCollections && (
                <div
                  className={collectionCardContainerClasses}
                  aria-busy="true"
                  aria-label="Loading cabinet collections"
                >
                  {Array.from({ length: 5 }).map((unused, index) => (
                    <Hydrator
                      key={`skeleton-card-${index}`}
                      id={`skeleton-card-${index}`}
                      delay={0}
                      scrollBuffer={100}
                      placeholder={(
                        <SkeletonCollectionCard isMobile={isMobile} />
                      )}
                    >
                      <SkeletonCollectionCard isMobile={isMobile} />
                    </Hydrator>
                  ))}
                </div>
              )}
              {
                (() => {
                  if (hasNoMatchingResults) {
                    return (
                      <div className="sui-py-8 sui-text-center">
                        <Typography variant="h4" weight="regular" className="sui-mb-2">
                          No matching collections found
                        </Typography>
                        <Typography variant="body-base" className="sui-text-secondary">
                          We couldn&apos;t find any collections that match your current cabinet
                          selection.
                        </Typography>
                      </div>
                    );
                  }
                  if (hasNoFilterResults) {
                    return (
                      <div className="sui-py-8 sui-text-center">
                        <Typography variant="h4" weight="regular" className="sui-mb-2">
                          No collections found
                        </Typography>
                        <Typography variant="body-base" className="sui-text-secondary">
                          Try adjusting your filters to see more results
                        </Typography>
                      </div>
                    );
                  }
                  return (
                    <>
                      <div
                        className={collectionCardContainerClasses}
                      >
                        {(displayDataSource).map((item, index) => (
                          <div
                            key={item.itemId || index}
                          >
                            <QueryProvider
                              dataSource="searchNav"
                              cacheKey="cabinet-collection-card"
                              defaultVariables={{ zipCode, storeId }}
                            >
                              <DynamicComponent pageType="collection">
                                <Hydrator
                                  id={`collection-card-${item.itemId}`}
                                  delay={0}
                                  scrollBuffer={100}
                                  placeholder={(
                                    <Placeholder
                                      width="100%"
                                      height="300px"
                                      color="#F5F5F5"
                                    />
                                  )}
                                >
                                  <ResponsiveCabinetCollectionCard
                                    collection={item}
                                    hoverableMedia={!isMobile}
                                    onPrimaryActionClick={handleSelectCollection}
                                    onSecondaryActionClick={
                                      isMatchingMode
                                        ? handleViewDetailsForMatchingMode
                                        : handleViewDetails
                                    }
                                    primaryActionButtonLabel={
                                      isMatchingMode ? 'Update to this Collection' : 'Select Collection'
                                    }
                                    secondaryActionButtonLabel="View"
                                    isMobile={isMobile}
                                  />
                                </Hydrator>
                              </DynamicComponent>
                            </QueryProvider>
                          </div>
                        ))}
                      </div>
                      {/* Load More button for matching mode */}
                      {shouldShowLoadMoreButton && (
                        <div className="sui-flex sui-justify-center sui-mt-4 sui-mb-4">
                          <Button
                            variant="secondary"
                            onClick={handleLoadMoreCollections}
                          >
                            Load More Collections
                          </Button>
                        </div>
                      )}
                    </>
                  );
                })()
              }
            </ErrorBoundary>
          </DrawerBody>
          <DrawerFooter />
        </div>
      </Drawer>
    </>

  );

});

CabinetCollectionDrawer.propTypes = {
  designId: string,
  open: bool.isRequired,
  onClose: func,
  onSelectCollection: func,
  isMatchingMode: bool,
  isMatchingLoading: bool,
  matchingCollections: arrayOf(shape({
    collectionId: string,
    collectionName: string,
    brandName: string,
    colorFamily: string,
    colorFinish: string,
    designStyleChoiceId: string,
    itemIdMap: shape({}),
    matchingRecordsCount: shape({})
  })),
  onApplyMatchingCollection: func
};

CabinetCollectionDrawer.defaultProps = {
  designId: undefined,
  onClose: undefined,
  onSelectCollection: undefined,
  isMatchingMode: false,
  isMatchingLoading: false,
  matchingCollections: null,
  onApplyMatchingCollection: undefined
};
